import { FunctionComponent, useContext } from 'react'
import { BackButtonLink } from '../../../display-elements-components/back-button-link'
import { Box, CircularProgress, Stack, Typography, useMediaQuery } from '@mui/material'
import { PageTitle } from '../../../display-elements-components/page-title'
import { TooltipButton } from '../../../miscellaneous-components/tooltip-button'
import gaiaTheme from '../../../../../root-components/theme'
import { IGroupDetail } from '../../../../../shared/services/group/group.model'
import { getDescription } from '../../../../../shared/data/group-tooltip.data'
import { useIsReportPeriodLocked } from '../../../../../hooks/use-is-report-period-locked'
import { UserDetailsContext } from '../../../../../context/user-details/user-details'
import { formatGroupTotal, getGroupTotalBasisLabel, getGroupTotalTonnes } from '../../../../../shared/utils/group-total-utils'

// The manual "Complete" flow was retired with the Progress Tracker: group done-ness is derived
// (data present, no incomplete tasks) rather than hand-ticked. Open readiness findings for the
// group appear as a banner above the tabs (EntityFindingsPanel on the group page), not here:
// the heading has no room for a full finding title next to the progress ring.
export const EmissionsOverviewGroupHeading: FunctionComponent<{
  group: IGroupDetail,
  getGroup: () => void
}> = ({
  group,
  getGroup
}) => {
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //Context
    const defaultMarketBased = useContext(UserDetailsContext).userDetails?.organisation?.defaultMarketBased
    // The group's total for the selected period, on the organisation's default Scope 2 basis. It is the
    // figure Analytics shows for this group, and it refreshes whenever the group is reloaded.
    const totalLabel = formatGroupTotal(getGroupTotalTonnes(group.totalEmissions, defaultMarketBased))
    const totalBasisLabel = getGroupTotalBasisLabel(group.scope, defaultMarketBased)
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    return (
      <>
        <BackButtonLink buttonText={'Back to group emissions'} link={'/emissions/overview'} />
        <Stack display='flex' flexDirection='row' alignItems='center' justifyContent='space-between'>
          <Stack display='flex' flexDirection='row' alignItems='center' sx={{ mt: group.reportActions.actionsTotal > 0 ? 0 : 2, mb: group.reportActions.actionsTotal > 0 ? 0 : 1 }}>
            <PageTitle title={group.title} />
            {!isMobileMode && <TooltipButton
              title={group.title}
              text={getDescription(group.title)}
              placement='right'
            />}
          </Stack>
          <Stack direction={'row'} justifyContent={'flex-end'} alignItems='center' gap={3}>
          {totalLabel &&
            <Stack alignItems='flex-end'>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {totalBasisLabel ? `Total emissions (${totalBasisLabel.toLowerCase()})` : 'Total emissions'}
              </Typography>
              <Typography variant='h6' sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                {totalLabel}
              </Typography>
            </Stack>}
          {!isMobileMode &&
            <Stack direction={'row'} justifyContent={'flex-end'} alignItems='center' gap={2}>
              {!isReportPeriodLocked && group.reportActions.actionsTotal > 0 &&
                <Box sx={{ position: 'relative', width: 75, height: 75 }}>
                  <CircularProgress
                    variant="determinate"
                    sx={{ color: (theme) => theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800] }}
                    size={75}
                    thickness={4}
                    value={100} />
                  <CircularProgress
                    variant="determinate"
                    value={group.reportActions.actionsTotal > 0 ? Math.floor(((group.reportActions.actionsTotal - group.reportActions.actionsOutstanding) / group.reportActions.actionsTotal) * 100) : 0}
                    size={75}
                    sx={{ position: 'absolute', top: 0, left: 0, }} />
                  <Box
                    sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', }}>
                    <Typography variant="caption" component="div" color="text.secondary" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                      {`${group.reportActions.actionsTotal > 0 ? Math.floor(((group.reportActions.actionsTotal - group.reportActions.actionsOutstanding) / group.reportActions.actionsTotal) * 100) : 0}%`}
                    </Typography>
                  </Box>
                </Box>}
            </Stack>}
          </Stack>
        </Stack>
      </>
    )
  }
